import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { VideoModal } from "./VideoModal";

/**
 * Reusable video carousel.
 * Props:
 *   items: [{ provider: 'youtube'|'vimeo', id, hash?, title?, brand?, thumb? }]
 *   testIdPrefix: string used for data-testid stems
 */
export const VideoCarousel = ({ items = [], testIdPrefix = "carousel" }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    dragFree: true,
    containScroll: "trimSnaps",
  });
  const [active, setActive] = useState(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onScroll = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
      const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()));
      setScrollProgress(progress);
    };
    onScroll();
    emblaApi.on("select", onScroll);
    emblaApi.on("scroll", onScroll);
    emblaApi.on("reInit", onScroll);
    return () => {
      emblaApi.off("select", onScroll);
      emblaApi.off("scroll", onScroll);
      emblaApi.off("reInit", onScroll);
    };
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollThumbWidth = Math.max(100 / Math.max(items.length, 1), 18);
  const scrollThumbOffset = scrollProgress * ((100 - scrollThumbWidth) / scrollThumbWidth) * 100;

  const thumbFor = (v) => {
    if (v.thumb) return v.thumb;
    if (v.provider === "youtube") return `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`;
    return null;
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-5">
        <div className="h-px flex-1 max-w-xs bg-white/15 overflow-hidden" aria-hidden="true">
          <div
            className="h-full bg-[#C5A059] transition-transform duration-200 ease-out"
            style={{
              width: `${scrollThumbWidth}%`,
              transform: `translateX(${scrollThumbOffset}%)`,
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={`${testIdPrefix}-prev`}
            onClick={scrollPrev}
            disabled={!canPrev}
            aria-label="Previous"
            className="w-9 h-9 flex items-center justify-center border border-border text-foreground/70 hover:text-foreground hover:border-foreground/60 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={15} strokeWidth={1.4} />
          </button>
          <button
            data-testid={`${testIdPrefix}-next`}
            onClick={scrollNext}
            disabled={!canNext}
            aria-label="Next"
            className="w-9 h-9 flex items-center justify-center border border-border text-foreground/70 hover:text-foreground hover:border-foreground/60 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={15} strokeWidth={1.4} />
          </button>
        </div>
      </div>

      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {items.map((v, i) => {
            const t = thumbFor(v);
            return (
              <button
                key={`${v.provider}-${v.id}-${i}`}
                data-testid={`${testIdPrefix}-item-${i}`}
                onClick={() => setActive(v)}
                className="embla__slide video-card group relative text-left overflow-hidden bg-black border border-white/10 hover:border-[#C5A059]/70 transition-colors"
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden">
                  {t ? (
                    <img
                      src={t}
                      alt={v.title || "Video thumbnail"}
                      loading="lazy"
                      className="video-thumb absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="video-thumb absolute inset-0 bg-gradient-to-br from-neutral-800 via-neutral-900 to-black" />
                  )}

                  {/* Play icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center bg-black/55 backdrop-blur-sm border border-white/40 group-hover:bg-[#C5A059] group-hover:border-[#C5A059] transition-all">
                      <Play size={18} strokeWidth={1.4} className="text-white ml-1" />
                    </div>
                  </div>

                  {/* Provider tag */}
                  <div className="absolute top-3 right-3 font-sans uppercase text-[9px] tracking-[0.28em] text-white/70 border border-white/20 px-2 py-1 bg-black/40">
                    {v.provider}
                  </div>
                </div>

                {/* Caption */}
                <div className="p-4 md:p-5 bg-black/60">
                  {v.brand && (
                    <div className="font-sans text-[10px] uppercase tracking-[0.32em] text-[#C5A059] mb-2 font-light">
                      {v.brand}
                    </div>
                  )}
                  <div className="font-sans text-white text-sm md:text-[15px] leading-snug font-light">
                    {v.title || "Untitled film"}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <VideoModal open={!!active} onClose={() => setActive(null)} video={active} />
    </div>
  );
};
